/**
 * Service worker caching policy (privacy first: this is an adult app that
 * is often used on shared devices).
 *
 * This module is imported by `vite.config.ts` and its objects are serialised
 * into `dist/sw.js` by Workbox. Two consequences:
 *  - every function below must be self-contained (no outer constants, no
 *    imports, no helpers), because only its source text reaches the worker;
 *  - the exported lists (`CACHEABLE_API_PATHS`, ...) exist for tests and docs,
 *    and `runtimeCaching.test.ts` keeps them in sync with the matchers.
 *
 * Policy:
 *  1. Only `GET` of four same-origin catalogue endpoints is ever cached
 *     (`StaleWhileRevalidate`, 1 day, 20 entries, status 200 only).
 *  2. Everything else under `/api`, `/sanctum` and `/broadcasting` is
 *     `NetworkOnly`: user, settings, sessions, prompts, couple, export,
 *     account, CSRF and socket auth are never stored and never answered from
 *     a cache. Non-GET methods are not routed at all, so they always reach the
 *     network and nothing can store them.
 *  3. Hashed build assets that are not precached (illustrations, a chunk above
 *     the size limit) are cached `CacheFirst` once they have been fetched.
 *     `clearRuntimeCaches` (src/pwa/clearCaches.ts) drops this cache and the
 *     catalogue cache when the person signs out.
 */
import type { RuntimeCaching } from 'workbox-build';

/** The only API paths whose responses may be cached (exact path, any query). */
export const CACHEABLE_API_PATHS = [
  '/api/categories',
  '/api/moods',
  '/api/fantasy-catalog',
  '/api/sex-toys',
] as const;

/** Path prefixes that must always go to the network. */
export const NETWORK_ONLY_PREFIXES = [
  '/api',
  '/sanctum',
  '/broadcasting',
] as const;

export const API_CATALOG_CACHE_NAME = 'hc-api-catalog';
export const ASSET_CACHE_NAME = 'hc-assets';

export const API_CATALOG_MAX_ENTRIES = 20;
export const API_CATALOG_MAX_AGE_SECONDS = 60 * 60 * 24;

/** Largest file the precache accepts; bigger files fall to the runtime rule. */
export const PRECACHE_MAX_FILE_BYTES = 1.5 * 1024 * 1024;

/**
 * App-shell files that go into the precache manifest: code, styles, fonts, the
 * manifest and the app icons. The hashed catalogue illustrations under
 * `assets/*.svg` are deliberately left out: precaching would download every
 * explicit illustration to a shared device before anyone opens (or is allowed
 * to open) it. They are cached only once viewed (see the `CacheFirst` rule).
 */
export const PRECACHE_GLOB_PATTERNS = [
  '**/*.{js,css,html,woff2,woff,webmanifest}',
  '*.svg',
  'icons/*.svg',
  'brand/*.svg',
];

/**
 * Navigations that must never be answered with the SPA shell. These are
 * backend routes (JSON, signed email links, the Filament admin).
 */
export const NAVIGATE_FALLBACK_DENYLIST: RegExp[] = [
  /^\/api(?:\/|$)/,
  /^\/sanctum(?:\/|$)/,
  /^\/broadcasting(?:\/|$)/,
  /^\/admin(?:\/|$)/,
  /^\/email\/verify(?:\/|$)/,
];

export const runtimeCaching: RuntimeCaching[] = [
  {
    // 1. The allowlist: GET of four catalogue endpoints, nothing else.
    urlPattern: ({ url, request, sameOrigin }) =>
      sameOrigin &&
      request.method === 'GET' &&
      [
        '/api/categories',
        '/api/moods',
        '/api/fantasy-catalog',
        '/api/sex-toys',
      ].indexOf(url.pathname) !== -1,
    handler: 'StaleWhileRevalidate',
    method: 'GET',
    options: {
      cacheName: API_CATALOG_CACHE_NAME,
      expiration: {
        maxEntries: API_CATALOG_MAX_ENTRIES,
        maxAgeSeconds: API_CATALOG_MAX_AGE_SECONDS,
      },
      cacheableResponse: { statuses: [200] },
      plugins: [
        {
          // Never store a response that carries cookies or says no-store.
          cacheWillUpdate: async ({ response }) => {
            if (
              response.headers.has('set-cookie') ||
              response.headers.has('set-cookie2')
            ) {
              return null;
            }
            const cacheControl = response.headers.get('cache-control') || '';
            if (/no-store/i.test(cacheControl)) {
              return null;
            }
            return response;
          },
        },
      ],
    },
  },
  {
    // 2. Catch-all, listed after the allowlist: the network, always.
    urlPattern: ({ url, sameOrigin }) =>
      sameOrigin &&
      /^\/(?:api|sanctum|broadcasting)(?:\/|$)/.test(url.pathname),
    handler: 'NetworkOnly',
    method: 'GET',
  },
  {
    // 3. Hashed, immutable build output that is not in the precache list.
    urlPattern: ({ url, request, sameOrigin }) =>
      sameOrigin &&
      request.method === 'GET' &&
      /^\/assets\/[^/]+-[\w-]{8}\.(?:js|css|svg|woff2?)$/.test(url.pathname),
    handler: 'CacheFirst',
    method: 'GET',
    options: {
      cacheName: ASSET_CACHE_NAME,
      expiration: { maxEntries: 60, maxAgeSeconds: 60 * 60 * 24 * 30 },
      cacheableResponse: { statuses: [200] },
    },
  },
];
